import styled from '@emotion/styled';
import {
  ILessonPlanStep,
  IQuestion,
  QuestionTypes,
  StepData,
} from '@taleemabad/db';
import {
  IGenericLessonPlanStep,
  PercentageLevel,
} from '@taleemabad/dsm/components';

const UPPERCASE_LETTER_REGEX = /([A-Z])/g;
const WHITESPACE_WORD_REGEX = /\s+(\w)/g;
const WHITESPACE_REGEX = /\s+/g;

/* eslint-disable-next-line */
export interface DsmUtilsProps { }

const StyledDsmUtils = styled.div`
  color: pink;
`;

export function DsmUtils(props: DsmUtilsProps) {
  return (
    <StyledDsmUtils>
      <h1>Welcome to DsmUtils!</h1>
    </StyledDsmUtils>
  );
}

export const isUrduText = (text: string): boolean => {
  try {
    if (typeof text !== 'string') {
      return false;
    }
    const urduRegex = /[\u0600-\u06FF]/g;
    const englishRegex = /[A-Za-z]/g;

    const urduCharMatches = text.match(urduRegex) || [];
    const englishCharMatches = text.match(englishRegex) || [];

    // If the number of Urdu characters is more than the English ones, it's considered Urdu
    return urduCharMatches.length > englishCharMatches.length;
  } catch (error) {
    console.error('Error in isUrduText:', error);
    return false;
  }
};

export const removeUrduCharacters = (text: string): string => {
  if (typeof text !== 'string') return text;
  const urduDotRegex = /۔/g;
  return text.replace(urduDotRegex, '').trim();
};
export const containsUrduCharacters = (text: string): boolean => {
  if (typeof text !== 'string') {
    return false;
  }
  const urduRegex = /[\u0600-\u06FF\u0750-\u077F]/;

  // If there is any Urdu character in the text, it's considered Urdu
  return urduRegex.test(text);
};
export const decodeHtmlEntities = (text: string) => {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
};

export const getAlphabetFromIndex = (index: number, isUrdu?: boolean) => {
  if (isUrdu) {
    const urduAlphabets = [
      'ا',
      'ب',
      'ج',
      'د',
      'ز',
      'ر',
      'ط',
      'ع',
      'ف',
      'ک',
      'ل',
      'م',
      'ص',
    ];
    return urduAlphabets[index] || ''; // For Urdu alphabets
  }
  return String.fromCharCode(65 + index); // For English letters (A-Z)
};

export const getQuestionTypeInfo = (
  type: string,
  groupType?: string,
): { type: string; message: string } | null => {
  const questionType = Object.values(QuestionTypes).find((key) => key === type);
  if (!questionType) {
    return null;
  }
  if (groupType === QuestionTypes.Comprehension) {
    return {
      type: 'Comprehension',
      message: 'Answer the questions based on the passage.',
    };
  }
  const QuestionTypeMessages: Record<QuestionTypes, string> = {
    [QuestionTypes.Mcq]: 'Choose the correct answer from the given options.',
    [QuestionTypes.Msq]:
      'Choose the multiple correct answers from the given options.',
    [QuestionTypes.Ftb]: 'Fill in the blanks with appropriate words.',
    [QuestionTypes.Saq]: 'Answer the following questions in short.',
    [QuestionTypes.Laq]: 'Answer the following questions in detail.',
    [QuestionTypes.Cis]: 'Correct the incorrect statements.',
    [QuestionTypes.Crq]: 'Provide a constructed response.',
    [QuestionTypes.Rrq]: 'Provide a restricted response.',
    [QuestionTypes.Reading]: 'Read and write based on the provided text.',
    [QuestionTypes.Creative]: 'Write creatively based on the given prompt.',
    [QuestionTypes.Speaking]: 'Prepare for speaking and listening tasks.',
    [QuestionTypes.TrueFalse]: 'Mark the statements as True or False.',
    [QuestionTypes.WordsMeanings]: 'Write meaning of the following words.',
    [QuestionTypes.SynonymsAntonyms]:
      'Match words with their synonyms and antonyms.',
    [QuestionTypes.WordsSentences]: 'Form sentences using the given words.',
    [QuestionTypes.MatchTheColumns]: 'Match the following columns.',
    [QuestionTypes.Investigate]: 'Investigate the given problem.',
    [QuestionTypes.Activity]: 'Perform the given activity.',
    [QuestionTypes.OralCommunication]: 'Perform the given oral communication.',
    [QuestionTypes.Comprehension]: 'Answer the questions based on the passage.',
    [QuestionTypes.SubQuestion]:
      'Look at the picture and write the beginning sound.',
  };

  const typeMap: Record<QuestionTypes, string> = {
    [QuestionTypes.Mcq]: 'Multiple Choice Question',
    [QuestionTypes.Msq]: 'Multiple Selection Question',
    [QuestionTypes.Ftb]: 'Fill In The Blanks',
    [QuestionTypes.Saq]: 'Short Answer Question',
    [QuestionTypes.Laq]: 'Long Answer Question',
    [QuestionTypes.Cis]: 'Correct the Incorrect Statement',
    [QuestionTypes.Crq]: 'Constructed Response',
    [QuestionTypes.Rrq]: 'Restricted Response',
    [QuestionTypes.Reading]: 'Reading and Writing',
    [QuestionTypes.Creative]: 'Creative Writing',
    [QuestionTypes.Speaking]: 'Speaking and Listening',
    [QuestionTypes.TrueFalse]: 'True or False',
    [QuestionTypes.WordsMeanings]: 'Words and Meanings',
    [QuestionTypes.SynonymsAntonyms]: 'Synonyms and Antonyms',
    [QuestionTypes.WordsSentences]: 'Words and Sentences',
    [QuestionTypes.MatchTheColumns]: 'Match the Columns',
    [QuestionTypes.Investigate]: 'Investigate',
    [QuestionTypes.Activity]: 'Activity',
    [QuestionTypes.OralCommunication]: 'Oral Communication',
    [QuestionTypes.Comprehension]: 'Comprehension',
    [QuestionTypes.SubQuestion]: 'Sub Question',
  };
  return {
    type: typeMap[questionType],
    message: QuestionTypeMessages[questionType],
  };
};

export const desiredQuestionsOrder = [
  QuestionTypes.Mcq,
  QuestionTypes.Msq,
  QuestionTypes.Ftb,
  QuestionTypes.TrueFalse,
  QuestionTypes.Cis,
  QuestionTypes.Crq,
  QuestionTypes.Rrq,
  QuestionTypes.Reading,
  QuestionTypes.Creative,
  QuestionTypes.Speaking,
  QuestionTypes.WordsMeanings,
  QuestionTypes.SynonymsAntonyms,
  QuestionTypes.WordsSentences,
  QuestionTypes.MatchTheColumns,
  QuestionTypes.Investigate,
  QuestionTypes.Activity,
  QuestionTypes.OralCommunication,
  QuestionTypes.Saq,
  QuestionTypes.Laq,
  QuestionTypes.Comprehension,
];

export const getSelectedQuestionsCountAndMarks = (
  questionType: string,
  selectedQuestions: IQuestion[],
) => {
  const filteredQuestions = selectedQuestions.filter((question) => {
    // If the question is part of a comprehension group, only include it if the type is "Comprehension" Otherwise, include it if the type matches
    if (question.group?.groupType === QuestionTypes.Comprehension) {
      return questionType === QuestionTypes.Comprehension;
    }
    return question.type === questionType;
  });

  const totalCount = filteredQuestions.length;
  const totalMarks = filteredQuestions.reduce(
    (sum, question) => sum + (question.score || 0),
    0,
  );
  return {
    count: totalCount,
    totalMarks: totalMarks,
  };
};
export const toRoman = (num: number, isUrdu?: boolean) => {
  if (isUrdu) {
    const urduLookup: { [key: number]: string } = {
      1: 'الف',
      2: 'ب',
      3: 'ج',
      4: 'د',
      5: 'ہ',
      6: 'و',
      7: 'ز',
      8: 'ح',
      9: 'ط',
      10: 'ی',
      // Add more mappings as needed
    };
    return urduLookup[num] || '';
  }

  const lookup: { [key: string]: number } = {
    M: 1000,
    CM: 900,
    D: 500,
    CD: 400,
    C: 100,
    XC: 90,
    L: 50,
    XL: 40,
    X: 10,
    IX: 9,
    V: 5,
    IV: 4,
    I: 1,
  };
  let roman = '';
  for (const i in lookup) {
    while (num >= lookup[i]) {
      roman += i;
      num -= lookup[i];
    }
  }
  return roman;
};

// These colors are specifically defined for Bloom's Taxonomy levels and are not part of the design system theme.
// They are added here as static values for consistency and clarity.
export const taxonomyLevels = [
  { name: 'Create', color: '#FF897D' },
  { name: 'Evaluate', color: '#E5BB00' },
  { name: 'Apply', color: '#29803E' },
  { name: 'Analyze', color: '#85C640' },
  { name: 'Remember', color: '#3F54A6' },
  { name: 'Understand', color: '#4A9BD3' },
];

export const questionTypes = [
  { name: 'seen', color: 'var(--md-sys-color-light-on-surface)' },
  { name: 'unseen', color: 'var(--md-sys-color-light-on-surface)' },
];

export const calculateBloomsAndCategoryPercentages = (
  questions: IQuestion[],
) => {
  const filteredQuestionsCount = questions.length;

  const taxonomyPercentages: PercentageLevel[] = taxonomyLevels.map((level) => {
    const count = questions.filter((q) =>
      q.questionTags?.some(
        (tag) => tag.toLowerCase() === level.name.toLowerCase(),
      ),
    )?.length;
    return {
      ...level,
      percentage:
        filteredQuestionsCount > 0
          ? Math.round((count / filteredQuestionsCount) * 100)
          : 0,
    };
  });

  const questionCategoryPercentages: PercentageLevel[] = questionTypes.map(
    (qType) => {
      const count = questions.filter(
        (q) => q.category?.toLowerCase() === qType.name.toLowerCase(),
      ).length;
      return {
        ...qType,
        percentage:
          filteredQuestionsCount > 0
            ? Math.round((count / filteredQuestionsCount) * 100)
            : 0,
      };
    },
  );

  return { taxonomyPercentages, questionCategoryPercentages };
};

export const transformQuestionTag = (tag: string) => {
  if (tag === 'seen') return 'Exercise';
  else return 'Conceptual';
};

export const formatString = (
  str: string,
  format: 'camel' | 'title',
): string => {
  if (!str) return '';

  const words = str
    .replace(UPPERCASE_LETTER_REGEX, ' $1')
    .toLowerCase()
    .trim()
    .split(' ');

  if (format === 'camel') {
    return words
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join('');
  }

  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const convertToCamelCaseFromSpaces = (text: string): string => {
  return text
    .toLowerCase()
    .replace(WHITESPACE_WORD_REGEX, (_, letter) => letter.toUpperCase())
    .replace(WHITESPACE_REGEX, '');
};

export const isStepData = (
  step: StepData | ILessonPlanStep,
): step is StepData => {
  return 'textBody' in step;
};

export const isFlnLessonPlan = (steps: IGenericLessonPlanStep[]): boolean => {
  return steps.some(
    (step) => Array.isArray(step.stepData) && step.stepData.some(isStepData),
  );
};


export const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};
